import { keepPreviousData } from '@tanstack/react-query';
import { graphql } from '~/graphql';
import { useGraphqlQuery } from '~/hooks/useGraphqlQuery';
import { useGraphqlMutation } from '~/hooks/useGraphqlMutation';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { PLATFORM_PRODUCT_QUERY_KEY } from '~/api/platformProducts';

export const ADMIN_PLATFORM_PRODUCTS_QUERY_KEY = 'adminPlatformProducts';

const adminSearchPlatformProductsQuery = graphql(`
  query AdminSearchPlatformProducts(
    $search: String!
    $page: Int!
    $size: Int!
  ) {
    adminSearchPlatformProducts(
      search: $search
      pageInput: { page: $page, size: $size }
    ) {
      content {
        id
        code
        name
        piecesPerBundle
        standardUnitsCode
        vehicleCapacities {
          capacity {
            value
            unit
          }
          transportVehicle {
            id
            name
          }
        }
        lengths {
          id
          lengthDisplayValue
          isNominal
          inventoryOnly
        }
      }
      paging {
        number
        size
        totalElements
        totalPages
      }
    }
  }
`);

const setPlatformProductLengthInventoryOnlyMutation = graphql(`
  mutation SetPlatformProductLengthInventoryOnly(
    $platformProductLengthId: ID!
    $inventoryOnly: Boolean!
  ) {
    setPlatformProductLengthInventoryOnly(
      platformProductLengthId: $platformProductLengthId
      inventoryOnly: $inventoryOnly
    ) {
      id
      inventoryOnly
    }
  }
`);

const setPlatformProductPiecesPerBundleMutation = graphql(`
  mutation SetPlatformProductPiecesPerBundle(
    $platformProductId: ID!
    $piecesPerBundle: Int!
  ) {
    setPlatformProductPiecesPerBundle(
      platformProductId: $platformProductId
      piecesPerBundle: $piecesPerBundle
    ) {
      id
      piecesPerBundle
    }
  }
`);

const setPlatformProductVehicleCapacityMutation = graphql(`
  mutation SetPlatformProductVehicleCapacity(
    $platformProductId: ID!
    $transportVehicleId: ID!
    $capacity: QuantityInput!
  ) {
    setPlatformProductVehicleCapacity(
      platformProductId: $platformProductId
      transportVehicleId: $transportVehicleId
      capacity: $capacity
    ) {
      capacity {
        value
        unit
      }
      transportVehicle {
        id
        name
      }
    }
  }
`);

type AdminProductSearchFilters = {
  search: string;
};

const useAdminSearchPlatformProducts = (
  page: number,
  pageSize: number,
  filters?: AdminProductSearchFilters
) => {
  const search = filters?.search ?? '';

  return useGraphqlQuery(
    adminSearchPlatformProductsQuery,
    [ADMIN_PLATFORM_PRODUCTS_QUERY_KEY, String(page), String(pageSize), search],
    { search, page, size: pageSize },
    { enabled: search.length > 0, placeholderData: keepPreviousData }
  );
};

export const useAdminSearchPlatformProductsPaginated = (
  pageSize: number,
  search: string
) =>
  usePaginatedQuery({
    fetchHook: useAdminSearchPlatformProducts,
    select: (data) => data?.adminSearchPlatformProducts,
    options: { pageSize, filters: { search: search.trim() } },
  });

export const useSetPlatformProductLengthInventoryOnly = () =>
  useGraphqlMutation(setPlatformProductLengthInventoryOnlyMutation, [
    ADMIN_PLATFORM_PRODUCTS_QUERY_KEY,
  ]);

export const useSetPlatformProductPiecesPerBundle = () =>
  useGraphqlMutation(setPlatformProductPiecesPerBundleMutation, [
    ADMIN_PLATFORM_PRODUCTS_QUERY_KEY,
    PLATFORM_PRODUCT_QUERY_KEY,
  ]);

export const useSetPlatformProductVehicleCapacity = () =>
  useGraphqlMutation(setPlatformProductVehicleCapacityMutation, [
    ADMIN_PLATFORM_PRODUCTS_QUERY_KEY,
    PLATFORM_PRODUCT_QUERY_KEY,
  ]);
